import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { firstValueFrom } from 'rxjs';
import * as QRCode from 'qrcode';
import { LogoUploadComponent } from '../../../components/logo-upload.component';
import { AgendaUploadComponent } from '../../../components/agenda-upload.component';
import {
  ActionConfirmData,
  ActionConfirmDialogComponent,
  ActionConfirmResult,
} from '../../abuse/action-confirm-dialog/action-confirm-dialog.component';
import { UsersService } from '../../../services/users-service.service';
import {
  blockingEventFields,
  buildEventForm,
  describeEventChanges,
  eventFieldError,
  eventFormPayload,
} from '../../../components/space-claim/event-form';
import { SpaceClaimHeaderComponent } from '../../../components/space-claim/space-claim-header.component';
import { FormSectionComponent } from '../../../components/space-claim/form-section.component';
import { QrCodeDisplayComponent } from '../../../components/space-claim/qr-code-display.component';
import { EventTooLongNoticeComponent } from '../../../components/space-claim/event-too-long-notice.component';
import { environment } from '../../../../environments/environment';
import { EventInsightDetailDto } from '../../../data-transfer-objects/events-insight.dto';

/** One setting, as the page prints it. */
interface Row {
  label: string;
  value: string | null;
  /** Rendered as a link rather than text. */
  href?: string | null;
  hint?: string;
  /** Empty here is worth noticing, not just blank. */
  warnWhenEmpty?: boolean;
}

/**
 * Everything one space is set to, on one page, in the claim wizard's clothes.
 *
 * The board answers "is this space working"; this answers "what is it set to",
 * which is a different question and was previously only answerable in the
 * Firestore console or by reading SQL. Every column the events table holds is
 * printed, including the empty ones - a setting that is missing is exactly the
 * thing worth seeing, and a page that hides its blanks cannot show it.
 *
 * Built from the claim page's own header, sections and QR panel rather than
 * new ones: an organizer's space should look the same to us as it does to
 * them, and one of these will drift if there are two.
 *
 * The QR code joins, it does not claim. Scanning registers whoever scanned it
 * into the space, which is what makes it useful to hold up at a venue - and
 * is not the code the claim wizard shows, which hands over ownership.
 */
@Component({
  selector: 'app-event-info-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    LogoUploadComponent,
    AgendaUploadComponent,
    SpaceClaimHeaderComponent,
    FormSectionComponent,
    QrCodeDisplayComponent,
    EventTooLongNoticeComponent,
  ],
  templateUrl: './event-info-dialog.component.html',
  styleUrls: ['./event-info-dialog.component.scss'],
})
export class EventInfoDialogComponent implements OnInit {
  isGeneratingQR = true;
  qrCodeData: string | null = null;
  qrError: string | null = null;
  readonly joinUrl: string;

  /* Edit mode. The same fields the organizer gets in the claim wizard, built
     from the same definition - see components/space-claim/event-form.ts. */
  editing = false;
  form: FormGroup | null = null;
  submitAttempted = false;
  saving = false;
  saveError: string | null = null;
  savedNotice: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public event: EventInsightDetailDto,
    private dialogRef: MatDialogRef<EventInfoDialogComponent>,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private usersService: UsersService
  ) {
    this.joinUrl = environment.joinEvent.getLink(this.event.id);
  }

  startEdit(): void {
    this.form = buildEventForm(this.fb, this.event);
    this.submitAttempted = false;
    this.saveError = null;
    this.savedNotice = null;
    this.editing = true;
  }

  cancelEdit(): void {
    this.editing = false;
    this.form = null;
    this.saveError = null;
  }

  fieldError(name: string): string {
    return this.form ? eventFieldError(this.form, name, this.submitAttempted) : '';
  }

  get blocking(): string[] {
    return this.form ? blockingEventFields(this.form) : [];
  }

  /**
   * Hands the cropper the logo that is already stored.
   *
   * Passed as a function so the claim wizard, which shares this uploader but
   * has no admin token, simply does not get the button.
   */
  readonly loadExistingLogo = async (): Promise<string | null> => {
    const image = await firstValueFrom(this.usersService.getEventLogoImage(this.event.id));
    return image?.dataUrl ?? null;
  };

  onLogoUploaded(logoUrl: string): void {
    this.form?.patchValue({ logoUrl });
    this.form?.markAsDirty();
  }

  onLogoRemoved(): void {
    this.form?.patchValue({ logoUrl: '' });
    this.form?.markAsDirty();
  }

  onAgendaUploaded(brochureUrl: string): void {
    this.form?.patchValue({ brochureUrl });
    this.form?.markAsDirty();
  }

  onAgendaRemoved(): void {
    this.form?.patchValue({ brochureUrl: '' });
    this.form?.markAsDirty();
  }

  /**
   * Save, after saying out loud whose space this is and what changes.
   *
   * The warning is the point. This is not our event: its organizer wrote these
   * fields and its members see the result in the app the moment it saves, with
   * nothing to tell them it was us. So the confirmation names the owner, counts
   * the people affected, and lists every field that would move - an admin
   * confirms specific changes, not the idea of saving.
   */
  async save(): Promise<void> {
    if (!this.form || this.saving) return;
    this.submitAttempted = true;
    this.saveError = null;

    if (this.form.invalid) {
      this.saveError = `Fix these first: ${this.blocking.join(', ')}.`;
      return;
    }

    const payload = eventFormPayload(this.form);
    const changes = describeEventChanges(this.event, payload);
    if (!changes.length) {
      this.saveError = 'Nothing has changed.';
      return;
    }

    const owner = this.event.owner?.name || 'nobody — this space is unclaimed';
    const members = this.event.memberCount;
    const confirmed = await firstValueFrom(
      this.dialog
        .open<ActionConfirmDialogComponent, ActionConfirmData, ActionConfirmResult>(
          ActionConfirmDialogComponent,
          {
            width: '560px',
            data: {
              title: 'Save changes to somebody else’s space?',
              description:
                `${this.event.title} is run by ${owner}. ` +
                `${members} member${members === 1 ? '' : 's'} will see this in the app ` +
                'straight away, and they will not be told the change came from us. ' +
                'The organizer is not notified.\n\n' +
                `${changes.length} change${changes.length === 1 ? '' : 's'}:\n` +
                changes.map((line) => `• ${line}`).join('\n'),
              confirmLabel: 'Save changes',
              danger: true,
            },
          }
        )
        .afterClosed()
    );
    if (!confirmed?.confirmed) return;

    this.saving = true;
    try {
      await firstValueFrom(this.usersService.updateEventDetails(this.event.id, payload));
      this.saving = false;
      /* Closing with the payload tells the page behind this one to reload, so
         what it shows is the saved row rather than this form's idea of it. */
      this.dialogRef.close({ saved: true });
    } catch (err: any) {
      this.saving = false;
      this.saveError =
        err?.error?.error ?? `Could not save: ${err?.message ?? err}`;
    }
  }

  ngOnInit(): void {
    this.buildQrCode();
  }

  buildQrCode(): void {
    this.isGeneratingQR = true;
    this.qrError = null;
    QRCode.toDataURL(this.joinUrl, {
      width: 300,
      margin: 2,
      color: { dark: '#12332F', light: '#FFFFFF' },
    })
      .then((dataUrl) => {
        this.qrCodeData = dataUrl;
        this.isGeneratingQR = false;
      })
      .catch((err) => {
        this.qrError = `Could not draw the QR code: ${err?.message ?? err}`;
        this.isGeneratingQR = false;
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  private date(iso: string | null): string | null {
    return iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium' }) : null;
  }

  private dateTime(iso: string | null): string | null {
    return iso
      ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
      : null;
  }

  get identity(): Row[] {
    const e = this.event;
    return [
      { label: 'Name', value: e.title },
      { label: 'Tag', value: e.tag, hint: 'What attendees type to find the space' },
      {
        label: 'Type',
        value: e.type,
        hint: e.type === 'Community' ? 'Ongoing, no dates' : 'A scheduled gathering',
      },
      {
        label: 'Visibility',
        value: e.isPrivate ? 'Private' : 'Public',
        hint: e.isPrivate ? 'Joining needs approval' : 'Anyone in the app can join',
      },
      { label: 'Description', value: e.description, warnWhenEmpty: true },
      { label: 'Event id', value: e.id },
    ];
  }

  get schedule(): Row[] {
    const e = this.event;
    const dated = e.type !== 'Community';
    return [
      { label: 'Starts', value: this.date(e.startDate), warnWhenEmpty: dated },
      { label: 'Ends', value: this.date(e.endDate), warnWhenEmpty: dated },
      { label: 'Registration opens', value: this.date(e.registrationStart) },
      { label: 'Registration closes', value: this.date(e.registrationEnd) },
      {
        label: 'Attendee limit',
        value: e.participantsLimit ? `${e.participantsLimit}` : 'No limit',
        hint: e.participantsLimit
          ? `${e.memberCount} of ${e.participantsLimit} taken`
          : `${e.memberCount} joined`,
      },
    ];
  }

  get place(): Row[] {
    const e = this.event;
    return [
      { label: 'Venue', value: e.venueName, warnWhenEmpty: true },
      { label: 'Address', value: e.locationDescription, warnWhenEmpty: true },
      { label: 'Named location', value: e.eventLocation?.name ?? e.eventLocation?.address ?? null },
      {
        label: 'Map pin',
        value: e.location ? `${e.location.lat.toFixed(5)}, ${e.location.lng.toFixed(5)}` : null,
        href: e.location
          ? `https://www.google.com/maps/search/?api=1&query=${e.location.lat},${e.location.lng}`
          : null,
        hint: e.location ? undefined : 'Without a pin the space never shows on the map',
        warnWhenEmpty: true,
      },
    ];
  }

  get lobby(): Row[] {
    const e = this.event;
    return [
      {
        label: 'Lobby mode',
        value: e.lobbyMode,
        hint:
          e.lobbyMode === 'GroupChat'
            ? 'Everyone talks in one room'
            : e.lobbyMode === 'Feed'
            ? 'Posts, not conversation'
            : 'Never set, so this space runs as a group chat',
        warnWhenEmpty: true,
      },
      { label: 'Members', value: `${e.memberCount}` },
      { label: 'Moderators', value: `${e.moderatorCount}` },
      {
        label: 'Waiting to join',
        value: `${e.pendingRequests}`,
        hint: e.pendingRequests ? 'Nobody has answered these' : undefined,
      },
    ];
  }

  get assets(): Row[] {
    const e = this.event;
    return [
      { label: 'Logo', value: e.logoUrl, href: e.logoUrl, warnWhenEmpty: true },
      { label: 'Website', value: e.websiteUrl, href: e.websiteUrl },
      { label: 'Brochure', value: e.brochureUrl, href: e.brochureUrl },
      { label: 'Stored deep link', value: e.dynamicLink, href: e.dynamicLink },
    ];
  }

  get claim(): Row[] {
    const e = this.event;
    return [
      { label: 'Owner', value: e.owner ? e.owner.name || e.owner.uid : 'Unclaimed' },
      { label: 'Claimed', value: this.dateTime(e.claimedAt) },
      { label: 'Invitation sent to', value: e.spaceClaimContactName },
      { label: 'Invitation email', value: e.spaceClaimContactEmail },
      { label: 'Claim page', value: e.spaceClaimUrl, href: e.spaceClaimUrl },
      { label: 'Claim logo', value: e.spaceClaimLogoUrl, href: e.spaceClaimLogoUrl },
      { label: 'Created', value: this.dateTime(e.createdAt) },
      { label: 'Last changed', value: this.dateTime(e.updatedAt) },
    ];
  }
}
